<template>
    <div class="dev-tools fixed bottom-2 left-2" style="z-index: 2000;">
      <q-btn
        round
        :color="isOpen ? 'accent' : 'primary'"
        icon="handyman"
        class="transition-all duration-300"
        :class="{ '!rounded-b-none': isOpen }"
        @click="isOpen = !isOpen"
      />
  
      <q-slide-transition>
        <div v-show="isOpen">
          <q-card flat bordered class="border-accent !rounded-tl-none">
            <q-card-section class="row items-center">
              <div class="text-bold">Dev Tools</div>
              <q-space />
              <q-btn flat round dense icon="close" @click="isOpen = false" />
            </q-card-section>
  
            <q-separator />
  
            <q-card-section class="flex flex-col gap-2">
              <div class="text-subtitle2">Se connecter en tant que</div>
              <q-btn-group>
                <q-btn
                  v-for="(label, role) in ROLE_NAMES"
                  :key="role"
                  :color="userConnected.role === role ? 'accent' : 'primary'"
                  :label="label"
                  size="sm"
                  class="flex-1"
                  no-caps
                  @click="handleConnectAs({ role })"
                />
              </q-btn-group>
              <div v-if="users.length" class="flex gap-2">
                  <q-select
                      v-model="selectedUserEmail"
                      :options="users"
                      option-label="email"
                      option-value="email"
                      emit-value
                      dense
                      filled
                      class="w-60"
                  />
                  <q-btn
                      size="sm"
                      label="Se connecter"
                      :disable="!selectedUserEmail"
                      :color="selectedUserEmail ? 'accent' : 'primary'"
                      no-caps
                      @click="handleConnectAs({ email: selectedUserEmail })"
                  />
              </div>
            </q-card-section>

            <q-separator inset class="!my-2"/>
            
            <q-card-section class="flex flex-col gap-2">
              <div class="text-subtitle2 flex gap-2">
                  Variables d'environnement
                  <q-btn size="xs" color="primary" :icon="showEnvVariables ? 'keyboard_arrow_up' : 'keyboard_arrow_down'" @click="showEnvVariables = !showEnvVariables" />
              </div>
              <div v-if="showEnvVariables" class="flex flex-col gap-1 max-w-96">
                    <div v-for="(value, key) in envVariables" :key="key" class="flex gap-1 text-sm relative cursor-pointer">
                      <div class="text-bold">{{ key }}</div>
                      <div class="text-grey-6 truncate max-w-60">
                        {{ value }}
                    </div>
                    <q-tooltip anchor="center right">{{ value }}</q-tooltip>
                  </div>
              </div>
            </q-card-section>

            <q-separator inset class="!my-2"/>

            <q-card-section class="flex flex-col gap-1">
              <div class="text-subtitle2">PhpMyAdmin</div>
                <q-btn no-caps size="sm" color="primary" @click="openPhpMyAdmin('SELECT * FROM user WHERE email = \'' + userConnected.user.email + '\'')">User</q-btn>
                <template v-if="currentLeadStore.lead.uuid">
                  <q-btn no-caps size="sm" color="primary" @click="openPhpMyAdmin('SELECT * FROM lead WHERE uuid = \'' + currentLeadStore.lead.uuid + '\'')">Lead</q-btn>
                  <q-btn no-caps size="sm" color="primary" @click="openPhpMyAdmin('SELECT * FROM market WHERE id = \'' + currentLeadStore.lead.market.id + '\'')">Market</q-btn>
                </template>
            </q-card-section>
          </q-card>
        </div>
      </q-slide-transition>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted } from 'vue'
  import { connectAs } from '@/api/dev'
  import { getUsers } from '@/api/user'
  import { useLocalStorage } from '@vueuse/core'
  import useToaster from '@/composables/useToaster'
  import { useConnectedUserStore } from '@/stores/connectedUser'
  import { ROLE_NAMES } from '@/constants/user'
  import { useProcessingLeadStore } from '@/stores/processingLead'
  
  
  const toaster = useToaster()
  const isOpen = useLocalStorage('dev-tools-is-open', false)
  const selectedUserEmail = ref('')
  const userConnected = useConnectedUserStore()
  const showEnvVariables = useLocalStorage('dev-tools-show-env-variables', false)
  const currentLeadStore = useProcessingLeadStore()
  const envVariables = ref(Object.fromEntries(Object.entries(import.meta.env).map(([key, value]) => [key, value.toString()])))
  
  const handleConnectAs = async ({ role, email }) => {
    try {
      await connectAs({ role, userEmail: email })
  
      toaster.success(`Connecté en tant que ${ROLE_NAMES[role]}`)
  
      window.location.href = '/home'
    } catch (_error) {
      toaster.error('Erreur lors du changement de rôle')
    }
  }
  
  const users = ref([])
  
  async function fetchUsers() {
      if(userConnected.role === 'teleoperator') return;

      const { data } = await getUsers()
      users.value = data
  }

  function openPhpMyAdmin(query) {
    window.open(`${import.meta.env.VITE_PHPMYADMIN_URL}&sql_query=${encodeURIComponent(query)}&show_query=1`, '_blank')
  }
  
  onMounted(async () => {
    await fetchUsers()
  })
  </script>
  
  <style lang="scss" scoped>
  .q-card__section {
      @apply p-2;
  }
  </style>
  
